import { WhereItIs } from "../components/WhereItIs";
import { useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fromState } from "@/lib/nav";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useState } from "react";
import { AddToCartButton } from "@/components/AddToCartButton";
import { PartPhotoCell } from "@/components/PartPhotoCell";
import { TruckFinder } from "@/components/TruckFinder";
import { NeedsPutawayBadge } from "@/components/NeedsPutawayBadge";
import { MatchedLine } from "@/components/MatchBadge";
import { useSearchEngine, useResultKeyboard, FilterRail } from "@/components/search/engine";
import { StagedPartFilters } from "@/components/search/PartTapFilters";
import { SearchBox, SearchTabs, BoxExtras, useSessionTab, useOwnTagOpen, useRecentSettle } from "@/components/search/SearchBox";
import { TruckResults, PartsDeadEnd, useTabCount } from "@/components/search/TabResults";
import { useRecentSearches } from "@/lib/recentSearches";
import { OnDonorBand } from "@/components/search/OnDonorBand";
import { DataplateMatchBand } from "@/components/search/DataplateMatchBand";
import { SyntheticDataBanner } from "@/components/SyntheticDataBanner";
import { SynonymSuggestButton } from "@/components/search/SynonymSuggest";
import { useScrollMemory } from "@/hooks/useScrollMemory";
import { statusLabel, statusColor, partStatusLabel, partStatusColor, isConsumedPart } from "@/lib/status";
import { titleCase } from "@/lib/text";
import { formatShort } from "@/lib/datetime";
import { toast } from "sonner";
import { Package, Truck, MapPin, Loader2, ShoppingCart, X, SlidersHorizontal, CornerDownLeft, ArrowUpDown, History, Recycle, Trash2, LogOut, Layers, ChevronRight } from "lucide-react";

export default function SearchPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { has } = useAuth();
  const canBuild = has("build_cart");
  const engine = useSearchEngine({ scope: "parts", pageSize: 50, defaultStatus: "available", box: "find_a_part", withCounts: true });
  const { f, setF, clearAll, activeKeys, res, loading, run, allStates, setAllStates, reportOpened } = engine;
  const recent = useRecentSearches();
  const addRecent = recent.add;
  const [tab, setTab] = useSessionTab("find_a_part", "parts");
  const trucksCount = useTabCount("trucks", f.q);
  const q = (f.q || "").trim();
  const onTrucks = !!q && tab === "trucks";
  const openPart = useCallback((p) => { reportOpened(p); addRecent(f.q); navigate(`/parts/${p.id}`, { state: fromState(location, "Find a Part") }); }, [navigate, location, reportOpened, addRecent, f.q]);
  const openTruck = useCallback((d) => { addRecent(f.q); navigate(`/donors/${d.id}`, { state: fromState(location, "Find a Part") }); }, [navigate, location, addRecent, f.q]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [truckToolOpen, setTruckToolOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const parts = useMemo(() => res?.parts || [], [res]);
  const total = res?.parts_total ?? 0;
  // NG-2: when the candidate window saturates the total is a LOWER BOUND — show "3,000+".
  const totalLabel = `${total.toLocaleString()}${res?.parts_total_is_lower_bound ? "+" : ""}`;
  // Presentation-only grouping: parts from ONE bulk-spawn run AND with the same name collapse into
  // one row. spawn_batch_id proves they're identical by construction; name keeps LH/RH as separate
  // rows (the variant dialog appends the variant to the name). Count = members currently in view
  // (this filter/page) — sold/moved/archived parts drop out of the results, so it never over-reads.
  const groups = useMemo(() => buildSpawnGroups(parts), [parts]);
  useScrollMemory(res != null);
  const openOwnTag = useOwnTagOpen("Find a Part", "search-input");
  const onEnter = (v) => {
    const t = (v || "").trim();
    if (!t) return;
    addRecent(t);
    setF({ q: t });
    openOwnTag(t);
  };
  // a recent search or a "did you mean": one tap RUNS it
  const runQ = (v) => { const t = (v || "").trim(); if (!t) return; addRecent(t); setF({ q: t }); };
  useRecentSettle(recent, res?.for_q);

  const quickAddToCart = useCallback(async (part) => {
    if (part.status !== "available" || part.reserved_cart_id) {
      toast.error(`${part.short_code} can't be quick-added (${statusLabel(part.status)}). Use its Add to Build/Order button — a part with no photo goes on with a reason.`); return;
    }
    try {
      const { data: drafts } = await api.get("/carts", { params: { status: "draft" } });
      if (!drafts.length) { toast.info("No open cart yet — use the row's Add to Cart to start one."); return; }
      const cart = drafts[0];
      try {
        const { data } = await api.post(`/carts/${cart.id}/add-part`, { part_id: part.id, force: false });
        toast.success(`\u2713 Added ${part.short_code} to ${data.cart_number}`); run();
      } catch (e) {
        const d = e.response?.data?.detail;
        if (d && typeof d === "object" && d.code === "on_other_cart") toast.warning(`${part.short_code} is already on another cart — use the row's Add to Cart to add anyway.`);
        else toast.error(formatApiErrorDetail(d));
      }
    } catch { toast.error("Couldn't load your carts."); }
  }, [run]);

  const { selIdx } = useResultKeyboard({
    items: parts,
    onOpen: openPart,
    onCart: canBuild ? quickAddToCart : null,
  });

  return (
    <div data-testid="search-page">
      <SyntheticDataBanner testid="search-synthetic-banner" />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Find a Part</h1>
          <p className="text-sm text-slate-500 mb-4">No part number? Search what you know, then narrow with the filters — it searches the whole yard, not just this page.</p>
        </div>
        <Sheet open={truckToolOpen} onOpenChange={setTruckToolOpen}>
          <SheetTrigger asChild>
            <button data-testid="search-trucks-tool"
              title="Look up a truck by serial/VIN or search the fleet by spec — a separate tool, doesn't affect this parts search"
              className="inline-flex items-center gap-2 rounded-xl border border-[#B01E28] bg-[#B01E28] px-4 h-12 text-sm font-bold text-white hover:bg-[#8B1820] shadow-sm transition-colors shrink-0">
              <Truck className="w-4 h-4" />Search Trucks
            </button>
          </SheetTrigger>
          <SheetContent side="right" data-testid="search-trucks-drawer" className="w-[92vw] sm:max-w-xl overflow-y-auto p-5">
            <SheetHeader className="mb-4">
              <SheetTitle className="font-display flex items-center gap-2"><Truck className="w-5 h-5 text-[#B01E28]" />Search Trucks</SheetTitle>
            </SheetHeader>
            <TruckFinder />
          </SheetContent>
        </Sheet>
      </div>

      <div className="xl:flex xl:gap-6 xl:items-start">        <aside data-testid="search-filter-rail" className="hidden xl:block xl:w-[340px] shrink-0 xl:sticky xl:top-20 xl:max-h-[calc(100vh-6rem)] xl:overflow-y-auto rounded-xl border border-slate-200 bg-slate-50/70 p-4 shadow-sm">
          <FilterRail engine={engine} pfx="" withSearch={false} />
        </aside>

        <div className="flex-1 min-w-0">
          <SearchBox value={f.q} onChange={(v) => setF({ q: v })} onEnter={onEnter} onPick={runQ} recent={recent}
            placeholder="What is it? e.g. fuel pump, alternator, OEM #, tag, truck stock #…">
            <BoxExtras engine={engine} />
          </SearchBox>
          {q && <SearchTabs tab={tab} onTab={setTab}
            counts={{ parts: (res?.for_q || "").trim() === q ? { count: total, lowerBound: res?.parts_total_is_lower_bound } : null, trucks: trucksCount }} />}

          {onTrucks ? <TruckResults q={q} onOpen={openTruck} onPick={runQ} /> : (<>
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
                <SheetTrigger asChild>
                  <Button data-testid="mobile-filters-btn" variant="outline" className="xl:hidden rounded-lg border-slate-300 h-12 px-4 text-sm">
                    <SlidersHorizontal className="w-4 h-4 mr-1.5" />Filters{activeKeys.length ? ` · ${activeKeys.length}` : ""}
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" data-testid="mobile-filters-drawer" className="w-[92vw] max-w-md overflow-y-auto p-5 pb-0">
                  <SheetHeader className="mb-3"><SheetTitle className="font-display">Filters</SheetTitle></SheetHeader>
                  {/* below 1280 px the taps stay in the drawer until "Show results" (S5, his yes) */}
                  {filtersOpen && <StagedPartFilters engine={engine} onDone={() => setFiltersOpen(false)} />}
                </SheetContent>
              </Sheet>
              {res != null && (
                <div data-testid="result-count" className="font-mono text-sm">
                  <span className={`font-bold ${total ? "text-[#B01E28]" : "text-slate-400"}`}>{totalLabel}</span>
                  <span className="text-slate-500"> {total === 1 ? "part" : "parts"}</span>
                  <span className="text-slate-400"> · {allStates ? "all states" : f.status ? `${statusLabel(f.status).toLowerCase()} only` : "every state still here"}</span>
                  {loading && <Loader2 className="inline w-3.5 h-3.5 animate-spin ml-1.5 text-slate-400" />}
                </div>
              )}
              <button data-testid="all-states-toggle" onClick={() => setAllStates(!allStates)}
                title="Deliberately include gone parts (sold / scrapped / recycled / archived) for traceability"
                className={`inline-flex items-center gap-1.5 rounded-full border px-4 h-12 text-sm font-semibold transition-colors ${allStates ? "border-slate-700 bg-slate-800 text-white" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}>
                <History className="w-4 h-4" />{allStates ? "Showing all states" : "Show gone parts"}
              </button>
            </div>
            {parts.length > 0 && (
              <div data-testid="keyboard-hint" className="hidden xl:flex items-center gap-2 text-[11px] text-slate-400 font-mono">
                <span className="inline-flex items-center gap-1"><ArrowUpDown className="w-3 h-3" />move</span>
                <span className="inline-flex items-center gap-1"><CornerDownLeft className="w-3 h-3" />open</span>
                {canBuild && <span className="inline-flex items-center gap-1"><kbd className="px-1 rounded border border-slate-300 bg-slate-50 text-slate-500 not-italic">C</kbd>cart</span>}
              </div>
            )}
          </div>

          {res == null ? <div className="p-10 text-center font-mono text-xs text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />LOADING…</div>
            : total === 0 ? <EmptyState f={f} res={res} activeKeys={activeKeys} setF={setF} clearAll={clearAll} onPick={runQ} setAllStates={setAllStates} />
            : (
              <div data-testid="search-results" className="border border-slate-200 bg-white rounded-xl overflow-hidden shadow-sm">
                {groups.map((g) => g.kind === "single"
                  ? <PartRow key={g.part.id} p={g.part} idx={g.idx} selected={g.idx === selIdx} top={!!q && g.idx === 0 && selIdx < 0} onOpen={openPart} onAdded={run} />
                  : <SpawnGroup key={g.key} group={g} selIdx={selIdx} open={!!expanded[g.key]}
                      onToggle={() => setExpanded((s) => ({ ...s, [g.key]: !s[g.key] }))} onOpen={openPart} onAdded={run} />)}
              </div>
            )}

          {/* Components still bolted to a truck — the rest of the answer, below the real parts.
              Same /truck-search engine as the Vehicles page, so a fix there lands here too. It
              gets the shelf count because it opens itself only when the shelf can't answer. */}
          {/* EVERY ENGINE CARRYING THE VALUE TYPED — in a truck or already pulled, each once, with
              the plate the match came from (his ask, 2026-10-16). */}
          <DataplateMatchBand q={f.q} />

          <OnDonorBand q={f.q} shelfCount={res?.for_q === (f.q || "") ? total : null} />
          </>)}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ f, res, activeKeys, setF, clearAll, onPick, setAllStates }) {
  const priority = ["engine", "model", "part_type_id", "year_max", "grade", "status", "brand", "make", "category_id", "q"];
  const narrowest = priority.find((k) => activeKeys.includes(k));
  return (
    <div data-testid="search-empty" className="border border-dashed border-slate-300 rounded-xl bg-slate-50/50 p-6 sm:p-10">
      {/* NEVER A DEAD END (S3): a look-alike or a spelling, the same words in other states, then the filters */}
      {f.q && (
        <div className="mb-6 max-w-xl mx-auto">
          <PartsDeadEnd q={f.q} res={res} onPick={onPick} testid="search-dead-end"
            onShowOther={() => setF({ status: "" })} onShowAll={() => setAllStates(true)} />
        </div>
      )}
      <div className="text-center">
      <Package className="w-8 h-8 text-slate-300 mx-auto mb-3" />
      <p className="font-display font-bold text-slate-700 text-lg">No parts match all of these filters</p>
      <p className="text-sm text-slate-500 mt-1 mb-4">Loosen one filter to widen the search — you don't need a part number to find it.</p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {narrowest && (
          <Button data-testid="empty-loosen" onClick={() => setF({ [narrowest]: "", ...(narrowest === "year_max" ? { year_min: "" } : {}) })} className="h-12 rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">
            <X className="w-4 h-4 mr-1.5" />Loosen a filter
          </Button>
        )}
        {f.q && (
          <Button data-testid="empty-clear-q" variant="outline" onClick={() => setF({ q: "" })} className="h-12 rounded-lg border-slate-300">
            Search by filters only (drop the text)
          </Button>
        )}
        <Button data-testid="empty-clear-all" variant="outline" onClick={clearAll} className="h-12 rounded-lg border-slate-300">Clear all filters</Button>
      </div>
      {f.q && (
        <div className="mt-4 flex flex-col items-center gap-1.5">
          <span className="text-xs text-slate-500">Does the crew call it something else?</span>
          <SynonymSuggestButton testid="empty-suggest-synonym" word={f.q} label={`Suggest another word for “${f.q}”`} />
        </div>
      )}
      </div>
    </div>
  );
}

// "Why/when it left" provenance chip on gone results (never shows cost — provenance only).
function GoneBadge({ p }) {
  if (!isConsumedPart(p)) return null;
  const scrap = p.scrap;
  const when = scrap?.at || p.updated_at;
  const date = when ? formatShort(when) : null;
  let Icon = LogOut, text, cls = "bg-zinc-100 text-zinc-700 border-zinc-200";
  if (scrap?.outcome === "recycled" || p.status === "recycled") {
    Icon = Recycle; cls = "bg-emerald-50 text-emerald-800 border-emerald-200";
    text = `Recycled${scrap?.recycle_batch_number ? ` · ${scrap.recycle_batch_number}` : ""}`;
  } else if (scrap || p.status === "scrap" || p.status === "defective") {
    Icon = Trash2; cls = "bg-red-50 text-[#B01E28] border-red-200";
    text = `Scrapped${scrap?.label ? ` · ${scrap.label}` : ""}`;
  } else if (p.status === "sold") {
    Icon = LogOut; cls = "bg-sky-50 text-sky-800 border-sky-200";
    text = `Sold${p.reserved_po ? ` · PO ${p.reserved_po}` : ""}`;
  } else {
    text = statusLabel(p.status);
  }
  return (
    <Badge data-testid={`search-gone-${p.short_code}`} variant="outline" className={`rounded-md text-sm font-semibold border ${cls}`}>
      <Icon className="w-4 h-4 mr-1" />{text}{date ? ` · ${date}` : ""}
    </Badge>
  );
}

// Collapse a bulk-spawn run (same spawn_batch_id + same name) into one row. Preserves result order
// by first appearance. A run with only one member currently in view renders as a normal single row.
function buildSpawnGroups(parts) {
  const idxOf = new Map();
  const order = [];
  parts.forEach((p, idx) => {
    const key = p.spawn_batch_id ? `${p.spawn_batch_id}::${(p.name || "").trim().toLowerCase()}` : null;
    if (!key) { order.push({ kind: "single", part: p, idx }); return; }
    if (!idxOf.has(key)) { idxOf.set(key, order.length); order.push({ kind: "group", key, name: p.name, batch: p.spawn_batch_id, members: [] }); }
    order[idxOf.get(key)].members.push({ part: p, idx });
  });
  return order.flatMap((e) =>
    e.kind === "group" && e.members.length === 1 ? [{ kind: "single", part: e.members[0].part, idx: e.members[0].idx }] : [e]);
}

function SpawnGroup({ group, selIdx, open, onToggle, onOpen, onAdded }) {
  const { name, members, batch } = group;
  const gid = `${batch}-${(name || "x").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`;
  const first = members[0].part;
  const truck = titleCase([first.year, first.make, first.model].filter(Boolean).join(" "));
  const availCount = members.filter((m) => m.part.status === "available" && !m.part.reserved_cart_id).length;
  const hasSelected = members.some((m) => m.idx === selIdx);
  return (
    <div data-testid={`search-spawn-group-${gid}`} className="border-t border-slate-100 first:border-t-0">
      <button data-testid={`search-spawn-toggle-${gid}`} onClick={onToggle}
        className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${open || hasSelected ? "bg-orange-50/60" : "hover:bg-slate-50/70"}`}>
        <ChevronRight className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
        <Layers className="w-4 h-4 text-[#E0781A] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-base text-slate-900">{titleCase(name) || <span className="italic text-slate-400">Unnamed part</span>}</span>
            <Badge className="rounded-md text-sm font-semibold bg-slate-800 text-white" data-testid={`search-spawn-count-${gid}`}>×{members.length} in view</Badge>
            {availCount !== members.length && <Badge variant="outline" className="rounded-md text-sm font-semibold text-slate-600">{availCount} available</Badge>}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5 text-sm text-slate-500">
            <Truck className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            {truck ? <span>off a <span className="font-medium text-slate-700">{truck}</span></span> : <span className="italic text-slate-400">No source truck on record</span>}
            <span className="text-slate-300">·</span>
            <span className="text-slate-400 font-mono text-[11px]">bulk run — tap to see each part &amp; tag</span>
          </div>
        </div>
      </button>
      {open && (
        <div data-testid={`search-spawn-members-${gid}`} className="bg-slate-50/40 pl-6 border-l-2 border-orange-100 ml-4">
          {members.map((m) => <PartRow key={m.part.id} p={m.part} idx={m.idx} selected={m.idx === selIdx} onOpen={onOpen} onAdded={onAdded} />)}
        </div>
      )}
    </div>
  );
}

function PartRow({ p, idx, selected, top, onOpen, onAdded }) {
  const unavail = p.status !== "available";
  const truck = titleCase([p.year, p.make, p.model].filter(Boolean).join(" "));
  const inCart = ["reserved", "picked"].includes(p.status);
  return (
    <div data-testid={`search-part-${p.short_code}`} data-back-row data-row-idx={idx} data-top-result={top ? "1" : undefined}
      className={`flex items-start gap-3 px-4 py-3.5 border-t border-slate-100 first:border-t-0 transition-colors ${selected ? "bg-orange-50 ring-1 ring-inset ring-[#E0781A]" : top ? "ring-2 ring-inset ring-[#E0781A] bg-orange-50/40" : unavail ? "bg-slate-50/40 hover:bg-slate-50/70" : "hover:bg-slate-50/70"}`}>
      <PartPhotoCell p={p} onChanged={onAdded} testidPrefix={`search-photo-${p.short_code}`} />
      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onOpen(p)}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-base text-slate-900 hover:text-[#B01E28] transition-colors">{titleCase(p.name) || <span className="italic text-slate-400">Unnamed part</span>}</span>
          {p.legacy && <Badge data-testid={`search-legacy-${p.short_code}`} className="rounded-md text-sm font-semibold bg-[#B01E28] text-white">Legacy</Badge>}
        </div>
        <MatchedLine match={p.match} testid={`search-matched-${p.short_code}`} />
        <div className="flex items-center gap-1.5 mt-0.5 text-sm text-slate-500">
          <Truck className="w-4 h-4 text-slate-400 shrink-0" />
          {truck ? <span>off a <span className="font-medium text-slate-700">{truck}</span></span> : <span className="italic text-slate-400">{p.donor_id ? "Off a truck with no year / make / model yet" : "No source truck on record"}</span>}
          {p.category_name && <span className="text-slate-500">{titleCase(p.category_name)}</span>}
        </div>
        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
          <Badge className={`rounded-md text-white text-sm font-semibold ${partStatusColor(p)}`}>{partStatusLabel(p)}</Badge>
          <GoneBadge p={p} />
          {p.grade && <Badge variant="outline" className="rounded-md text-sm font-semibold">Grade {p.grade}</Badge>}
          <NeedsPutawayBadge part={p} />
          {p.in_transit && <Badge className="rounded-md text-sm font-semibold bg-indigo-600 text-white"><Truck className="w-4 h-4 mr-1" />In Transit</Badge>}
          {inCart && (
            <Badge data-testid={`search-incart-${p.short_code}`} className="rounded-md text-sm font-semibold bg-amber-100 text-amber-800 border border-amber-200">
              <ShoppingCart className="w-4 h-4 mr-1" />In Cart{p.reserved_cart_number ? ` · ${p.reserved_cart_number}` : ""}{p.reserved_po ? ` · PO ${p.reserved_po}` : ""}
            </Badge>
          )}
          {p.location && <WhereItIs location={p.location} size="md" testid={`search-where-${p.short_code}`} />}
          {p.retail_price != null && <span className="font-mono text-sm text-slate-700">${p.retail_price}</span>}
          <span className="font-mono text-sm text-slate-500">{p.tag_code || p.short_code}</span>
        </div>
      </div>
      <div className="shrink-0 pt-0.5" onClick={(e) => e.stopPropagation()}><AddToCartButton part={p} compact big onAdded={onAdded} /></div>
    </div>
  );
}
